/**
 * GDELT Intelligence Panel — topic-filtered GDELT event feed.
 *
 * Filters GDELT events by intelligence topic (Military, Cyber, Nuclear, Sanctions)
 * using keyword matching on titles. Displays a scrollable feed with severity badges,
 * timestamps, and source domains. Click → emit event:selected for detail view.
 */

import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import { relativeTime } from '@/utils/time';
import { SEVERITY_CSS_COLORS } from '@/utils/severity';
import type { NormalizedEvent } from '@/sources/base-source';

type GdeltTopic = 'all' | 'military' | 'cyber' | 'nuclear' | 'sanctions';

const TOPIC_TABS: { label: string; value: GdeltTopic }[] = [
  { label: 'All', value: 'all' },
  { label: 'Military', value: 'military' },
  { label: 'Cyber', value: 'cyber' },
  { label: 'Nuclear', value: 'nuclear' },
  { label: 'Sanctions', value: 'sanctions' },
];

/** Keyword sets for each topic filter. Matched against lowercased title + tags. */
const TOPIC_KEYWORDS: Record<Exclude<GdeltTopic, 'all'>, string[]> = {
  military: [
    'military', 'army', 'navy', 'air force', 'defense', 'defence',
    'troops', 'soldier', 'missile', 'weapon', 'artillery', 'tank',
    'drone', 'airstrike', 'bombing', 'invasion', 'nato', 'warship',
    'combat', 'battalion', 'regiment', 'deployment', 'pentagon',
    'armed forces', 'warfare', 'ammunition', 'fighter jet',
  ],
  cyber: [
    'cyber', 'hack', 'hacker', 'ransomware', 'malware', 'phishing',
    'data breach', 'vulnerability', 'zero-day', 'ddos', 'botnet',
    'spyware', 'apt', 'threat actor', 'cybersecurity', 'infosec',
    'exploit', 'encryption', 'firewall', 'intrusion',
  ],
  nuclear: [
    'nuclear', 'uranium', 'plutonium', 'enrichment', 'warhead',
    'reactor', 'iaea', 'nonproliferation', 'atomic', 'fissile',
    'centrifuge', 'radiation', 'radioactive', 'missile defense',
    'icbm', 'ballistic', 'deterrence', 'arms control',
  ],
  sanctions: [
    'sanction', 'sanctions', 'embargo', 'blacklist', 'ofac',
    'asset freeze', 'trade restriction', 'export control',
    'restricted entity', 'designated', 'penalty', 'ban',
    'punitive measures', 'financial restriction',
  ],
};

/** Check if a GDELT event matches a topic by scanning title + tags. */
function matchesTopic(event: NormalizedEvent, topic: GdeltTopic): boolean {
  if (topic === 'all') return true;
  const keywords = TOPIC_KEYWORDS[topic];
  const searchText = (event.title + ' ' + (event.tags ?? []).join(' ')).toLowerCase();
  return keywords.some((kw) => searchText.includes(kw));
}

export function createGdeltIntelPanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'gdelt-intel-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

  // Tab bar
  const tabBar = document.createElement('div');
  tabBar.style.cssText = `
    display: flex; gap: 4px; padding: 8px 12px; flex-wrap: wrap;
    border-bottom: 1px solid var(--border-primary); flex-shrink: 0;
  `;
  container.appendChild(tabBar);

  // Summary stats line
  const statsLine = document.createElement('div');
  statsLine.style.cssText = `
    padding: 6px 12px; font-size: 11px; color: var(--text-muted);
    border-bottom: 1px solid var(--border-primary); flex-shrink: 0;
  `;
  container.appendChild(statsLine);

  // Content area
  const contentArea = document.createElement('div');
  contentArea.style.cssText = 'padding: 0; flex: 1; overflow-y: auto;';
  container.appendChild(contentArea);

  const cleanupFns: (() => void)[] = [];
  let renderPending = false;
  let activeTopic: GdeltTopic = 'all';

  function scheduleRender(): void {
    if (renderPending) return;
    renderPending = true;
    requestAnimationFrame(() => {
      renderPending = false;
      render();
    });
  }

  function renderTabs(): void {
    tabBar.textContent = '';
    for (const tab of TOPIC_TABS) {
      const btn = document.createElement('button');
      btn.style.cssText = `
        padding: 4px 10px; border-radius: 12px; border: 1px solid var(--border-primary);
        font-size: 11px; cursor: pointer; transition: all 150ms;
        background: ${activeTopic === tab.value ? 'var(--accent)' : 'var(--bg-secondary)'};
        color: ${activeTopic === tab.value ? '#fff' : 'var(--text-secondary)'};
      `;
      btn.textContent = tab.label;
      btn.onclick = () => {
        activeTopic = tab.value;
        renderTabs();
        scheduleRender();
      };
      tabBar.appendChild(btn);
    }
  }

  function getGdeltEvents(): NormalizedEvent[] {
    const events = appStore.getState().events;
    return events.filter((e) => e.source === 'gdelt');
  }

  function render(): void {
    const allGdelt = getGdeltEvents();
    const filtered = allGdelt.filter((e) => matchesTopic(e, activeTopic));

    // Sort by timestamp descending (most recent first)
    filtered.sort((a, b) => {
      const ta = a._timestampMs ?? new Date(a.timestamp).getTime();
      const tb = b._timestampMs ?? new Date(b.timestamp).getTime();
      return tb - ta;
    });

    // Update stats
    statsLine.textContent = `${filtered.length} of ${allGdelt.length} GDELT articles`;

    contentArea.textContent = '';

    if (filtered.length === 0) {
      const empty = document.createElement('div');
      empty.style.cssText = 'padding: 24px 12px; text-align: center; color: var(--text-muted); font-size: 12px;';
      empty.textContent = allGdelt.length === 0
        ? 'No GDELT data loaded yet. Data refreshes every 15 minutes.'
        : `No articles match the "${activeTopic}" filter.`;
      contentArea.appendChild(empty);
      return;
    }

    // Limit displayed items to prevent DOM bloat
    const MAX_ITEMS = 200;
    const displayItems = filtered.slice(0, MAX_ITEMS);

    for (const event of displayItems) {
      const row = document.createElement('div');
      row.style.cssText = `
        padding: 8px 12px; border-bottom: 1px solid var(--border-primary);
        cursor: pointer; transition: background 150ms;
      `;
      row.onmouseenter = () => { row.style.background = 'var(--bg-tertiary)'; };
      row.onmouseleave = () => { row.style.background = ''; };
      row.onclick = () => eventBus.emit('event:selected', event);

      // Title line
      const title = document.createElement('div');
      title.style.cssText = 'font-size: 12px; color: var(--text-primary); line-height: 1.4; margin-bottom: 4px;';
      title.textContent = event.title;
      row.appendChild(title);

      // Meta line: severity badge + domain + timestamp + country
      const meta = document.createElement('div');
      meta.style.cssText = 'display: flex; align-items: center; gap: 6px; font-size: 10px; color: var(--text-muted); flex-wrap: wrap;';

      const severity = event.severity ?? 'info';
      const badge = document.createElement('span');
      badge.style.cssText = `
        padding: 1px 6px; border-radius: 8px; font-size: 9px; font-weight: 600;
        color: #fff; background: ${SEVERITY_CSS_COLORS[severity]};
      `;
      badge.textContent = severity.toUpperCase();
      meta.appendChild(badge);

      const domain = (event.raw as Record<string, unknown>)?.domain;
      if (typeof domain === 'string') {
        const domainEl = document.createElement('span');
        domainEl.textContent = domain;
        meta.appendChild(domainEl);
      }

      const timeEl = document.createElement('span');
      timeEl.textContent = relativeTime(event.timestamp);
      meta.appendChild(timeEl);

      if (event.location?.country) {
        const countryEl = document.createElement('span');
        countryEl.textContent = event.location.country;
        meta.appendChild(countryEl);
      }

      row.appendChild(meta);
      contentArea.appendChild(row);
    }

    if (filtered.length > MAX_ITEMS) {
      const more = document.createElement('div');
      more.style.cssText = 'padding: 8px 12px; text-align: center; color: var(--text-muted); font-size: 11px;';
      more.textContent = `${filtered.length - MAX_ITEMS} more articles not shown`;
      contentArea.appendChild(more);
    }
  }

  // Initial render
  renderTabs();
  render();

  // Subscribe to event changes
  cleanupFns.push(
    appStore.subscribe(
      (s) => s.events,
      () => scheduleRender(),
    ),
  );

  // Cleanup method for teardown
  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}
